import { images } from 'app/design/images';
import { Box, Column, Row } from 'app/design/primitives/box';
import { SearchInput } from 'app/design/primitives/input/search';
import { ChannelList } from './channel-list';
import { Text } from 'app/design/primitives/text';
import { PCImage } from 'app/design/primitives/image';
import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect, useEffectEvent, useState } from 'react';
import { FlatListWithTabs } from 'app/atoms/flatlist-with-tabs';
import { useDeviceWidth } from 'app/hooks/use-device-size';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Button } from 'app/design/primitives/button';
import type { TopLoadingIndicatorState } from 'app/components/top-loading-indicator';
import { TopLoadingIndicator, useTopLoadingIndicator } from 'app/components/top-loading-indicator';
import { useUISize, usePageTitle, isMobileNative } from 'app/utils/web-helpers';
import { Dot } from 'app/design/icons/dot';
import { useRoute, useNavigation, useNavigationOptions } from 'app/hooks/navigation';
import { NO_CHANNEL_SELECTED } from 'app/network/chat/channel';
import { useAtomValue } from 'jotai';
import { currentTutorialAtom } from 'app/atoms';
import { type Listing } from 'app/types/listing';
import { ChannelListHeader } from './channel-list-header';
import { useTutorialRef, useTutorials } from 'app/components/tutorial-overlay';
import { Tutorial, TutorialRef } from 'app/types/tutorial';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconArrowLeft, IconEdit } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { ChannelsListType } from './all-channels.constants';
import type { TFunction } from 'i18next';

export { ChannelsListType } from './all-channels.constants';

const theme = createDSTheme();

const AllChannelsHeaderRight = function AllChannelsHeaderRight() {
  const { isLargeUI, isSmallUI } = useUISize();
  const navigation = useNavigation<'all-channels'>();
  const { viewRef } = useTutorialRef(TutorialRef.ChatCreationButton);
  const { t } = useTranslation('accessibility');

  if (isLargeUI) return null;
  return (
    <Pressable
      accessibilityLabel={t('chat.newConversation')}
      accessibilityRole="button"
      collapsable={false}
      ref={viewRef}
      onPress={() => navigation.navigate('new-conversation', undefined, { pop: true })}
      style={{
        paddingRight: isSmallUI && !isMobileNative ? theme.spacing['spacing.general.x4'] : undefined,
      }}
    >
      <IconEdit strokeWidth={ICON_STROKE_WIDTH} size={26} color={theme.colors['white-label.header.on-header']} />
    </Pressable>
  );
};

const renderAllChannelsHeaderRight = () => <AllChannelsHeaderRight />;

const selectStandaloneValue = <T,>(
  standalone: boolean | undefined,
  localValue: T | undefined,
  routeValue: T | undefined,
) => (standalone ? localValue : routeValue);

const AllChannelsTabs = ({
  archivedLoadingState,
  fixedContainerWidth,
  recentLoadingState,
  selectedTabState,
  tabs,
}: {
  archivedLoadingState: TopLoadingIndicatorState;
  fixedContainerWidth: number | undefined;
  recentLoadingState: TopLoadingIndicatorState;
  selectedTabState: [ChannelsListType, Dispatch<SetStateAction<ChannelsListType>>];
  tabs: Record<ChannelsListType, React.JSX.Element>;
}) => (
  <FlatListWithTabs
    i18nNS="all-channels"
    initialTab={selectedTabState[0]}
    screenName="all-channels"
    swipeDisabled
    tabs={tabs}
    viewAboveTabs={<TopLoadingIndicator isLoading={recentLoadingState.isLoading || archivedLoadingState.isLoading} />}
    flexListAlign="flex-start"
    fixedContainerWidth={fixedContainerWidth ?? 530}
  />
);

const AllChannelsDesktopLayout = ({
  children,
  count,
  hasBackButton,
  listingId,
  onBack,
  onOpenNewConversation,
  onPressChannelTitle,
  t,
  userId,
}: {
  children: React.ReactNode;
  count: number | undefined;
  hasBackButton: boolean;
  listingId: Listing['id'] | undefined;
  onBack: () => void;
  onOpenNewConversation: () => void;
  onPressChannelTitle: (() => void) | undefined;
  t: TFunction;
  userId: string | undefined;
}) => {
  const canPressTitle = Boolean(onPressChannelTitle && !hasBackButton);
  const Wrapper = canPressTitle ? Pressable : React.Fragment;
  const wrapperProps = canPressTitle ? { onPress: onPressChannelTitle } : {};

  return (
    <Column
      alignSelf="stretch"
      flex={1}
      width="100%"
      borderWidth={1}
      borderColor="surface.inverse.tertiary-neutral"
      borderRadius="radius.lg"
      overflow="hidden"
    >
      <Wrapper {...wrapperProps}>
        <Row
          style={{ backgroundColor: theme.colors['white-label.header.bg'] }}
          paddingVertical="spacing.general.x4"
          paddingHorizontal="spacing.general.x4"
          alignItems="center"
          justifyContent="space-between"
        >
          <Row alignItems="center" gap="spacing.general.x1">
            {hasBackButton ? (
              <Pressable onPress={onBack} style={{ marginRight: theme.spacing['spacing.general.x2'] }}>
                <IconArrowLeft strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['white-label.header.on-header']} />
              </Pressable>
            ) : (
              <Text style={{ color: theme.colors['white-label.header.on-header'] }} fontWeight="bold" fontSize={16}>
                {t('title')}
              </Text>
            )}
            <ChannelListHeader {...{ listingId, userId }} serverCount={count} isNavigationOptionHeader={false} />
          </Row>
          <Row alignItems="center" gap="spacing.general.x2">
            <Pressable
              accessibilityLabel={t('chat.newConversation', { ns: 'accessibility' })}
              accessibilityRole="button"
              onPress={onOpenNewConversation}
            >
              <IconEdit
                strokeWidth={ICON_STROKE_WIDTH}
                size={26}
                color={theme.colors['white-label.header.on-header']}
              />
            </Pressable>
          </Row>
        </Row>
      </Wrapper>
      {children}
    </Column>
  );
};

export const AllChannelsScreenComponent = function AllChannelsScreenComponent({
  fixedContainerWidth,
  standalone,
  onPressChannelTitle,
}: {
  fixedContainerWidth?: number;
  standalone?: boolean;
  onPressChannelTitle?: () => void;
}) {
  const { isLargeUI } = useUISize();
  const currentTutorial = useAtomValue(currentTutorialAtom);
  const { t } = useTranslation('all-channels');
  const route = useRoute<'all-channels'>();
  const currentRoute = useRoute<'all-channels' | 'one-channel'>();
  const navigation = useNavigation<'all-channels'>();
  const { startTutorial, hasCompletedTutorial } = useTutorials();

  useNavigationOptions(navigation, {
    headerRight: renderAllChannelsHeaderRight,
  });

  // Local filter state for standalone mode (e.g. inside MessageFooter)
  const [localFilters, setLocalFilters] = useState<{
    listingId?: Listing['id'];
    userId?: string;
    count?: number;
  }>({});

  const listingId = selectStandaloneValue(standalone, localFilters.listingId, currentRoute.params?.listingId);
  const userId = selectStandaloneValue(standalone, localFilters.userId, currentRoute.params?.userId);
  const count = selectStandaloneValue(standalone, localFilters.count, currentRoute.params?.count);

  const handleGroupHeaderPress = (filter: { listingId?: Listing['id']; userId?: string; count: number }) => {
    setLocalFilters(filter);
  };

  const handleBack = () => {
    if (standalone) {
      setLocalFilters({});
    } else {
      navigation.navigate('one-channel', { url: NO_CHANNEL_SELECTED }, { pop: true });
    }
  };
  const openNewConversation = () => {
    if (navigation.getState().routeNames.includes('new-conversation')) {
      navigation.navigate('new-conversation', undefined, { pop: true });
      return;
    }

    navigation.navigate('chat', { screen: 'new-conversation' }, { pop: true });
  };

  const [chatCreationTutorialShown, setChatCreationTutorialShown] = useState(false);
  const hasBackButton = Boolean(userId ?? listingId);
  const showChatCreationTutorial = useEffectEvent(() => {
    if (!navigation.isFocused()) return;
    setChatCreationTutorialShown(true);
    startTutorial(Tutorial.ChatCreation);
  });

  useEffect(() => {
    const shouldShowTutorial =
      isMobileNative &&
      !isLargeUI &&
      !standalone &&
      !hasBackButton &&
      !chatCreationTutorialShown &&
      !currentTutorial &&
      !hasCompletedTutorial(Tutorial.ChatCreation);

    if (!shouldShowTutorial) return;

    const timeout = setTimeout(() => {
      showChatCreationTutorial();
    }, 3000);

    return () => clearTimeout(timeout);
  }, [chatCreationTutorialShown, currentTutorial, hasBackButton, hasCompletedTutorial, isLargeUI, standalone]);

  const navigateToOneChannelIfLarge = useEffectEvent((width?: number) => {
    const shouldNavigate = width === undefined ? isLargeUI : width >= theme.breakpoints.medium;
    if (!shouldNavigate) return;

    navigation.navigate(
      'one-channel',
      {
        url: currentRoute.params?.url ?? NO_CHANNEL_SELECTED,
        listingId: currentRoute.params?.listingId,
        userId: currentRoute.params?.userId,
        count: currentRoute.params?.count,
      },
      { pop: true },
    );
  });

  // Desktop does not support the all-channels route; navigate as soon as the
  // dimensions event crosses into the large layout instead of waiting for a render.
  useEffect(() => {
    let wasLargeWindow = false;

    if (standalone) return;

    navigateToOneChannelIfLarge();
    if (isMobileNative) return;

    wasLargeWindow = window.innerWidth >= theme.breakpoints.medium;
    const navigateOnWindowResize = () => {
      const isLargeWindow = window.innerWidth >= theme.breakpoints.medium;
      if (isLargeWindow && !wasLargeWindow) {
        navigateToOneChannelIfLarge(window.innerWidth);
      }
      wasLargeWindow = isLargeWindow;
    };

    window.addEventListener('resize', navigateOnWindowResize);
    return () => window.removeEventListener('resize', navigateOnWindowResize);
  }, [standalone]);

  const selectedTabState = useState(
    !standalone && route.params?.archiveTab ? ChannelsListType.Archived : ChannelsListType.Recent,
  );

  const recentLoadingState = useTopLoadingIndicator();
  const archivedLoadingState = useTopLoadingIndicator();

  const setPageTitle = usePageTitle('all-channels');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const tabs = {
    [ChannelsListType.Recent]: (
      <RenderedList
        listingId={listingId}
        userId={userId}
        count={count}
        isLoadingState={recentLoadingState}
        selectedTabState={selectedTabState}
        onGroupHeaderPress={standalone ? handleGroupHeaderPress : undefined}
      />
    ),
    [ChannelsListType.Archived]: (
      <RenderedList
        archived
        listingId={listingId}
        userId={userId}
        count={count}
        isLoadingState={archivedLoadingState}
        selectedTabState={selectedTabState}
        onGroupHeaderPress={standalone ? handleGroupHeaderPress : undefined}
      />
    ),
  };
  const tabsContent = (
    <AllChannelsTabs {...{ archivedLoadingState, fixedContainerWidth, recentLoadingState, selectedTabState, tabs }} />
  );

  // TODO: On others page white background appears. Need to investigate
  if (isLargeUI) {
    return (
      <AllChannelsDesktopLayout
        {...{ count, hasBackButton, listingId, onPressChannelTitle, t, userId }}
        onBack={handleBack}
        onOpenNewConversation={openNewConversation}
      >
        {tabsContent}
      </AllChannelsDesktopLayout>
    );
  }

  return tabsContent;
};

const RenderedList = function RenderedList({
  selectedTabState,
  archived = false,
  isLoadingState,
  listingId,
  userId,
  count,
  onGroupHeaderPress,
}: {
  archived?: boolean;
  selectedTabState: [ChannelsListType, Dispatch<SetStateAction<ChannelsListType>>];
  isLoadingState: TopLoadingIndicatorState;
  listingId?: Listing['id'];
  userId?: string;
  count?: number;
  onGroupHeaderPress?: (filter: { listingId?: Listing['id']; userId?: string; count: number }) => void;
}) {
  const { t } = useTranslation('all-channels');
  const [search, setSearch] = useState('');
  const [, setSelectedTab] = selectedTabState;
  const width = useDeviceWidth();
  const { isSmallUI, isLargeUI } = useUISize();
  const [isSelecting, setIsSelecting] = useState(false);
  const route = useRoute();
  const currentRoute = route.name;
  const onPressSelect = () => setIsSelecting((selecting) => !selecting);
  const exitEditMode = () => setIsSelecting(false);

  return (
    <View style={{ flex: 1, width: isLargeUI ? '100%' : width, paddingHorizontal: 0 }}>
      <ChannelList
        showsVerticalScrollIndicator={isLargeUI}
        ListEmptyComponent={
          <Column marginBottom="spacing.general.x8" width="100%">
            <PCImage
              alignSelf="center"
              height={250}
              marginBottom="spacing.general.x8"
              source={{ uri: images.messaging }}
              style={{ width: '100%' }}
            />
            <Text alignSelf="center" fontSize={16} textAlign="center" variant="bodyBase" width="75%">
              {t(`empty.secondLine.${archived ? 'archived' : 'recent'}`)}
            </Text>
          </Column>
        }
        ListFooterComponent={
          !archived ? <GoToArchivedChannelsButton onPress={() => setSelectedTab(ChannelsListType.Archived)} /> : null
        }
        ListHeaderComponent={
          <View style={{ width: '100%', alignSelf: 'stretch' }}>
            <Row
              gap="spacing.general.x2"
              marginBottom="spacing.general.x4"
              style={{
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Column flex={1} minWidth={0}>
                <SearchInput onSubmit={setSearch} initialValue={search} />
              </Column>

              {!archived && (isSmallUI || currentRoute === 'one-channel') ? (
                <Pressable
                  onPress={onPressSelect}
                  style={{
                    backgroundColor: theme.colors['surface.inverse.tertiary-neutral'],
                    borderRadius: theme.spacing['spacing.general.x2'],
                    paddingHorizontal: theme.spacing['spacing.general.x2'],
                    height: 30 + theme.spacing['spacing.general.x2'],
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {isSelecting ? (
                    <Text color="text.primary" variant="bodyBase">
                      {t('cancelSelection')}
                    </Text>
                  ) : (
                    <Row justifyContent="center" style={{ gap: 2 }}>
                      <Dot color={theme.colors['text.primary']} />
                      <Dot color={theme.colors['text.primary']} />
                      <Dot color={theme.colors['text.primary']} />
                    </Row>
                  )}
                </Pressable>
              ) : null}
            </Row>
          </View>
        }
        archived={archived}
        isLoadingState={isLoadingState}
        search={search}
        isSelecting={isSelecting}
        exitEditMode={exitEditMode}
        listingId={listingId}
        userId={userId}
        count={count}
        onGroupHeaderPress={onGroupHeaderPress}
      />
    </View>
  );
};

const GoToArchivedChannelsButton = function GoToArchivedChannelsButton({ onPress }: { onPress?: () => void }) {
  const { t } = useTranslation('all-channels');
  return (
    <Column
      alignContent="center"
      backgroundColor="surface.tertiary"
      borderRadius="radius.lg"
      gap="spacing.general.x4"
      margin="spacing.general.x4"
      paddingHorizontal="spacing.general.x4"
      paddingVertical="spacing.general.x4"
    >
      <Text color="text.primary" fontSize={22} fontWeight="700" textAlign="center" variant="bodyBase">
        {t('footer.title')}
      </Text>
      <Text color="text.primary" fontSize={16} textAlign="center" variant="bodyBase">
        {t('footer.text')}
      </Text>
      <Box alignItems="center">
        <Button
          onPress={onPress}
          style={{
            height: 32,
            borderRadius: 16,
            backgroundColor: theme.colors['surface.inverse.default'],
            paddingHorizontal: theme.spacing['spacing.general.x4'],
          }}
          variant="small-pill"
        >
          <Text color="surface.default" variant="bodyBase">
            {t('footer.button')}
          </Text>
        </Button>
      </Box>
    </Column>
  );
};
